<template>
  <div class="mb-4 h-fit space-y-6 lg:col-span-2 lg:mb-0">
    <LazyMarketingContentVideo
      v-if="hasYoutubeUrl"
      class="w-full"
      :blok="blokWithYoutubeUrl"
    />
    <UCard
      :ui="{
        root: 'bg-neutral-900/40 mt-1 backdrop-blur-lg overflow-hidden',
        body: 'p-0 sm:p-0',
      }"
    >
      <template #header>
        <div class="flex items-center justify-between">
          <h3
            class="flex items-center space-x-2 text-lg font-extrabold text-white"
          >
            <span>Upcoming Dates</span>
            <!-- Display total upcoming events -->
            <span class="text-sm font-extrabold text-yellow-400">
              ({{ filteredSchedule.length }})
            </span>
          </h3>
          <UIcon
            name="i-lucide-calendar-days"
            class="size-6 shrink-0 text-yellow-400"
          />
        </div>
      </template>
      <div class="relative divide-y divide-neutral-800">
        <!-- Use paginatedSchedule instead of props.blok.schedule -->
        <div
          v-for="(schedule, index) in paginatedSchedule"
          :key="index"
          class="group relative isolate h-full w-full overflow-hidden"
        >
          <UBadge
            v-if="schedule.badge"
            :label="schedule.badge"
            class="absolute top-4 right-4 font-bold"
          />
          <div class="relative z-[2] flex items-center space-x-6 p-6">
            <div class="text-3xl font-black text-yellow-400 drop-shadow-lg">
              {{ (currentPage - 1) * itemsPerPage + index + 1 }}
            </div>
            <div class="space-y-1">
              <div
                class="text-xs font-extrabold tracking-wide text-neutral-400 uppercase"
              >
                Meeting Point
              </div>
              <div class="-mt-1.5 text-lg font-extrabold drop-shadow-lg">
                {{ schedule.meeting_point }}
              </div>
              <div
                class="flex space-x-1 text-sm font-semibold text-neutral-400 drop-shadow-lg"
              >
                <UIcon
                  name="i-lucide-clock"
                  class="size-4 shrink-0 text-yellow-400"
                />
                <div class="-mt-0.5">
                  {{ formatEventTime(schedule.start) }} -
                  {{ formatEventTime(schedule.end) }}
                </div>
              </div>
              <div
                v-if="schedule.notes"
                class="flex space-x-1 text-sm font-medium text-neutral-400 drop-shadow-lg"
              >
                <UIcon
                  name="i-lucide-info"
                  class="size-4 shrink-0 text-yellow-400"
                />
                <div class="-mt-0.5 line-clamp-2 group-hover:line-clamp-none">
                  {{ schedule.notes }}
                </div>
              </div>
            </div>
            <UTooltip
              v-if="schedule.what_three_words"
              :text="schedule.what_three_words"
              :content="{
                align: 'center',
                side: 'left',
                sideOffset: 8,
              }"
            >
              <UButton
                :to="`https://what3words.com/${schedule.what_three_words}`"
                target="_blank"
                variant="soft"
                color="error"
                class="ml-auto"
              >
                <div class="flex -rotate-12 items-center -space-x-2.5">
                  <span class="sr-only">What Three Words</span>
                  <UIcon name="i-lucide-slash" class="size-4 text-red-500" />
                  <UIcon name="i-lucide-slash" class="size-4 text-red-500" />
                  <UIcon name="i-lucide-slash" class="size-4 text-red-500" />
                </div>
              </UButton>
            </UTooltip>
          </div>
        </div>
      </div>
      <div
        v-if="currentPage === totalPages"
        class="border-t border-neutral-800 p-6 text-center"
      >
        <UIcon name="i-lucide-calendar-plus-2" class="size-6 text-yellow-400" />
        <p class="text-sm font-semibold text-neutral-400">
          More dates to be announced
        </p>
      </div>
      <template v-if="filteredSchedule.length > itemsPerPage" #footer>
        <!-- Pagination Controls -->
        <div class="flex items-center justify-between">
          <!-- Previous Button -->
          <UButton
            variant="subtle"
            icon="i-lucide-chevron-left"
            :disabled="currentPage === 1"
            @click="prevPage"
          />

          <!-- Page Indicator -->
          <div class="text-sm font-bold text-neutral-400">
            Page {{ currentPage }} of {{ totalPages }}
          </div>

          <!-- Next Button -->
          <UButton
            variant="subtle"
            icon="i-lucide-chevron-right"
            :disabled="currentPage === totalPages"
            @click="nextPage"
          />
        </div>
      </template>
    </UCard>
    <UButton
      v-if="blok.products && blok.products.length !== 0"
      size="xl"
      class="w-full justify-center text-center"
      @click="changeTab"
    >
      RSVP Now
    </UButton>
    <div v-if="blok.faqs && blok.faqs.length !== 0" class="hidden lg:block">
      <LazyMarketingContentFaqs :blok="blok" :faqs="blok.faqs" />
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type {
  EventStoryblok,
  ScheduleStoryblok,
} from '~/types/component-types-sb';

dayjs.extend(utc);
dayjs.extend(timezone);

const props = defineProps<{
  blok: EventStoryblok;
}>();

const emits = defineEmits<{
  (e: 'no-rsvp', value: boolean): void;
}>();

const hasYoutubeUrl = computed(() => !!props.blok.youtube_url?.url);
const blokWithYoutubeUrl = computed(
  () =>
    props.blok as EventStoryblok & {
      youtube_url: NonNullable<EventStoryblok['youtube_url']>;
    }
);

// Helper function to ensure consistent time formatting
const formatEventTime = (dateString: string | undefined) => {
  if (!dateString) return '';
  // Parse the date as UTC and convert to local time
  return dayjs.utc(dateString).local().format('ddd DD MMM HH:mm');
};

// Reactive variables for pagination
const currentPage = ref(1);
const itemsPerPage = 3;

// Filtered schedule based on current and future events
const filteredSchedule = computed(() => {
  const now = dayjs();
  return (
    props.blok.schedule?.filter((schedule: ScheduleStoryblok) =>
      dayjs.utc(schedule.end).local().isAfter(now)
    ) ?? []
  );
});

// Paginated schedule based on the current page
const paginatedSchedule = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredSchedule.value.slice(start, end);
});

// Update the totalPages computed property to reflect the filtered schedule
const totalPages = computed(() =>
  Math.ceil(filteredSchedule.value.length / itemsPerPage)
);

if (props.blok.products?.length === 0) {
  emits('no-rsvp', false);
}

// Handlers for next and previous buttons
const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};
const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const changeTab = () => {
  const router = useRouter();
  const route = useRoute();

  router.push({
    query: {
      ...route.query, // Spread the existing query parameters
      tab: 'rsvp', // Update the tab parameter
    },
  });
};
</script>
